import InfoIcon from '@mui/icons-material/Info';
import { useBusinessInvoices } from 'lib/api/business';
import type { InvoiceRow } from 'lib/api/business/types';
import CessionHistoryDialog from 'lib/common/Dialogs/CessionHistoryDialog';
import { Filter } from 'lib/common/GlobalSearcher';
import PageHeader from 'lib/designSystem/PageHeader';
import TableWithPagination from 'lib/designSystem/TableWithPagination';
import { formatDate } from 'lib/functions/utils';
import React, { useCallback, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useParams } from 'react-router-dom';
import { showBusinessNetwork } from 'services/redux/businessNetworkSlice';

import { buildBusinessInvoicesQuery } from '../../utils/queryBuilder';
import {
  getCessionHistoryColumnConfig,
  useCessionHistoryDialog,
} from '../../utils/useCessionHistoryDialog';

const PAGE_SIZE = 10;
const DEFAULT_PAGINATION = {
  page: 1,
  pageSize: PAGE_SIZE,
  total: 0,
  totalPages: 0,
};

const BusinessInvoicesReceived = () => {
  const dispatch = useDispatch();
  const { id } = useParams<{ id: string }>();

  const [inputFilters, setInputFilters] = useState({
    startDate: null as string | null,
    endDate: null as string | null,
    folio: '',
    businessIdentifier: '',
    businessName: '',
  });

  const [searchState, setSearchState] = useState({
    filters: {
      startDate: null as string | null,
      endDate: null as string | null,
      folio: '',
      businessIdentifier: '',
      businessName: '',
    },
    page: 1,
    pageSize: PAGE_SIZE,
  });

  const query = buildBusinessInvoicesQuery(
    {
      ...searchState.filters,
      stakeholderIdentifier: searchState.filters.businessIdentifier,
    },
    { page: searchState.page, pageSize: searchState.pageSize },
  );
  const { data, isLoading, isFetching } = useBusinessInvoices(
    dispatch,
    id,
    'received',
    query,
  );

  const {
    openCessionHistoryDialog,
    setOpenCessionHistoryDialog,
    selectedCessions,
    handleShowCessionHistory,
  } = useCessionHistoryDialog(data?.data ?? []);

  const searchReceivedInvoices = useCallback(
    async ({ page, pageSize }: { page: number; pageSize: number }) => {
      setSearchState((prev) => ({ ...prev, page, pageSize }));
      return {
        data: data?.data ?? [],
        pagination: data?.pagination ?? DEFAULT_PAGINATION,
      };
    },
    [data?.data, data?.pagination],
  );

  const triggerSearch = useCallback(async () => {
    setSearchState({
      filters: { ...inputFilters },
      page: 1,
      pageSize: PAGE_SIZE,
    });
  }, [inputFilters]);

  const filters: Filter[] = [
    {
      label: 'Fecha de emisión',
      type: 'date',
      value: inputFilters.startDate,
      setter: (v: string | null) =>
        setInputFilters((prev) => ({ ...prev, startDate: v })),
      dateKey: 'startDate',
    },
    {
      label: 'Fecha de emisión',
      type: 'date',
      value: inputFilters.endDate,
      setter: (v: string | null) =>
        setInputFilters((prev) => ({ ...prev, endDate: v })),
      dateKey: 'endDate',
    },
    {
      label: 'Folio',
      type: 'text',
      value: inputFilters.folio,
      setter: (v: string) => setInputFilters((prev) => ({ ...prev, folio: v })),
    },
    {
      label: 'Rut emisor',
      type: 'text',
      value: inputFilters.businessIdentifier,
      setter: (v: string) =>
        setInputFilters((prev) => ({ ...prev, businessIdentifier: v })),
    },
    {
      label: 'Nombre emisor',
      type: 'autocomplete',
      value: inputFilters.businessName,
      key: 'businessName',
      setter: (v: string) =>
        setInputFilters((prev) => ({ ...prev, businessName: v ?? '' })),
    },
  ];

  const handleShowBusinessDetails = (rowId: string) => {
    const row = (data?.data ?? []).find((x: InvoiceRow) => x.id === rowId);
    if (!row) return;
    dispatch(
      showBusinessNetwork({
        id: row.businessIdentifier,
        name: row.businessName,
      }),
    );
  };

  return (
    <div>
      <PageHeader.WithTitleAndActions
        iconType="invoice"
        title="Facturas Recibidas."
        subtitle="Aquí verás las facturas que has recibido."
        globalSearcherProps={{
          filters,
          triggerSearch,
          dispatch,
          disableSearch: isLoading || isFetching,
        }}
      />
      <TableWithPagination
        tableSize="small"
        finalSumRow
        exportToFile="xlsx"
        dataHeaders={[
          { key: 'folio', label: 'Folio' },
          { key: 'code', label: 'Cód.' },
          { key: 'businessName', label: 'Emisor' },
          {
            key: 'businessIdentifier',
            label: 'Rut Emisor',
            handleAction: handleShowBusinessDetails,
            icon: <InfoIcon fontSize="small" />,
          },
          { key: 'amount', label: 'Monto', type: 'money' },
          {
            key: 'issuedDate',
            label: 'Emisión',
            type: 'date',
            formatter: (value: Date) => formatDate(value, 'dd-mm-yyyy'),
          },
          {
            key: 'receiptDate',
            label: 'Recepción',
            type: 'eight-days-date',
          },
          { key: 'creditNoteAmount', label: 'NC', type: 'money-colored' },
          { key: 'status', label: 'Estado', type: 'status-chip' },
          {
            key: 'cancelDate',
            label: 'Reclamo',
            type: 'date-colored',
          },
          {
            key: 'confirmDate',
            label: 'Acuse',
            type: 'date-colored-ok',
          },
          getCessionHistoryColumnConfig(handleShowCessionHistory),
          {
            key: 'lastCessionDate',
            label: 'Fecha de cesión',
            type: 'date',
            formatter: (value: Date) => formatDate(value, 'dd-mm-yyyy'),
          },
        ]}
        data={data?.data ?? []}
        loading={isLoading || isFetching}
        setData={() => {}}
        searchData={searchReceivedInvoices}
        setPagination={() => {}}
        pagination={data?.pagination ?? DEFAULT_PAGINATION}
        lastElement
      />
      <CessionHistoryDialog
        open={openCessionHistoryDialog}
        onClose={() => setOpenCessionHistoryDialog(false)}
        cessions={selectedCessions}
      />
    </div>
  );
};

export default BusinessInvoicesReceived;
